/* eslint-disable import/order, import/first */
import Sentry from './lib/Sentry';

// Initialize Sentry before loading any other packages so we can get Sentry notifications
// if there are any errors during load.
const environmentVars = window.__ENV__ || {}; // eslint-disable-line no-underscore-dangle
const {buildTarget, commitHash, sentryDsn, appEnvironment} = environmentVars;
Sentry.initialize({buildTarget, commitHash, dsn: sentryDsn, environment: appEnvironment});

// If the override is set (primarily CI) then set the magic webpack variable
// which will cause any client side generated script urls to use this host
// instead of what was set during the build via PUBLIC_PATH
if (environmentVars.overridePublicPath) {
  /* eslint-disable no-undef, camelcase */
  __webpack_public_path__ = environmentVars.overridePublicPath;
}
import React from 'react';
import {ApolloClient, ApolloLink, createHttpLink, InMemoryCache} from '@apollo/client';
import {setContext} from '@apollo/client/link/context';
import {BatchHttpLink} from '@apollo/client/link/batch-http';
import {BrowserRouter} from 'react-router-dom';
import {hydrateRoot} from 'react-dom/client';

import fetch from 'unfetch';
import 'unfetch/polyfill';
import {CacheProvider} from '@emotion/react';
import createCache from '@emotion/cache';
import arrayIncludes from 'array-includes';
import {loadableReady} from '@loadable/component';
import {createDirectConnectClient} from './components/direct-connect/DirectConnectProvider';
import './styles/global.css';
import '../packages/semantic/dist/semantic.min.css';
import './styles/semantic-overrides.css';
import '@fontsource-variable/figtree/index.css';
import '@fontsource/figtree/latin-ext.css';
import '@fontsource/figtree/latin-ext-italic.css';
// eslint-disable-next-line import/extensions
import ApolloSentryLink from './graphql/ApolloSentryLink';
import createApolloCache, {possibleTypes} from './graphql/createApolloCache';
import DetectLoggedOutLink from './graphql/DetectLoggedOutLink';
import App from './App';
import generateTrackingHeaders from './util/generateTrackingHeaders';
import {createUploadLink} from 'apollo-upload-client';
/* eslint-enable import/order, import/first */

// adds polyfill for array.includes if not defined
arrayIncludes.shim();

const {clientFederationGraphqlHost, isTest, ezRailsDomain} = environmentVars;

const graphqlEndpoint = `${clientFederationGraphqlHost}/graphql`;

const authLink = setContext((_, {headers}) => ({
  headers: {
    ...headers,
    ...generateTrackingHeaders(),
  },
}));

const productionBatchLink = new BatchHttpLink({
  fetch,
  uri: graphqlEndpoint,
  credentials: 'include',
});

const testBatchLink = createHttpLink({fetch, uri: graphqlEndpoint, credentials: 'include'});
const batchLink = isTest ? testBatchLink : productionBatchLink;
const apolloSentryLink = ApolloSentryLink();
const {apolloLink: detectLoggedOutLink, loggedOutListener} = DetectLoggedOutLink();

const cache = createApolloCache().restore(
  // eslint-disable-next-line no-underscore-dangle
  window.__APOLLO_STATE__
);

const client = new ApolloClient({
  cache,
  link: ApolloLink.from([apolloSentryLink, detectLoggedOutLink, authLink, batchLink]),
  name: 'ezcater-ezmanage-ui',
  version: commitHash,
});

const directConnectApolloClient = createDirectConnectClient({
  environment: appEnvironment,
  // eslint-disable-next-line no-underscore-dangle
  initialState: window.__DIRECT_CONNECT_APOLLO_STATE__,
  graphQLEndpoint: environmentVars.clientDCGraphQLEndpoint,
});

// Necesary as of writing because the federation gateway is unable to properly
// handle and forward uploads to ez-rails
// ez-rails gets an info hash without a way to read the actual file
const uploadLink = createUploadLink({
  fetch,
  uri: `${ezRailsDomain}/graphql/management`,
  credentials: 'include',
});
const ezRailsUploadClient = new ApolloClient({
  cache: new InMemoryCache({possibleTypes}),
  link: ApolloLink.from([apolloSentryLink, detectLoggedOutLink, authLink, uploadLink]),
});

/* eslint-disable no-underscore-dangle */
loadableReady(() => {
  const cacheProviderCache = createCache({key: 'ez'});
  // enable compatibility mode
  cacheProviderCache.compat = true;
  const container = document.getElementById('root');
  hydrateRoot(
    container,
    <CacheProvider value={cacheProviderCache}>
      <App
        apolloClient={client}
        assignmentsConfiguration={window.__ASSIGNMENTS_CONFIGURATION__ || {}}
        directConnectApolloClient={directConnectApolloClient}
        ezRailsUploadClient={ezRailsUploadClient}
        environmentVars={environmentVars}
        loggedOutListener={loggedOutListener}
        router={BrowserRouter}
      />
    </CacheProvider>
  );
});
/* eslint-enable no-underscore-dangle */

if (module.hot) {
  module.hot.accept();
}
